import { useSession } from "@/services/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type {
  CustomerContact,
  CreateContactInput,
  UpdateContactInput,
  ListContactsQuery,
  ListContactsResponse,
  ParsedContactData,
} from "./types";

const API_URL = import.meta.env.VITE_API_URL;

// Query keys
export const contactsKeys = {
  all: ["contacts"] as const,
  lists: () => [...contactsKeys.all, "list"] as const,
  list: (filters: ListContactsQuery) =>
    [...contactsKeys.lists(), filters] as const,
  details: () => [...contactsKeys.all, "detail"] as const,
  detail: (id: string) => [...contactsKeys.details(), id] as const,
};

// Get single contact
export const useContact = (id: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: contactsKeys.detail(id),
    queryFn: async (): Promise<CustomerContact> => {
      const { data } = await axios.get<CustomerContact>(
        `${API_URL}/api/v2/customers/me/address-book/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!id && !!token,
  });
};

// Create contact
export const useCreateContact = () => {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateContactInput): Promise<CustomerContact> => {
      const { data } = await axios.post<CustomerContact>(
        `${API_URL}/api/v2/customers/me/address-book`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      if (customer?.id) {
        void queryClient.invalidateQueries({
          queryKey: ["customers", customer.id, "contacts"],
        });
      }
    },
  });
};

// Update contact
export const useUpdateContact = () => {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateContactInput;
    }): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/me/address-book/${id}`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: contactsKeys.detail(variables.id),
      });
      if (customer?.id) {
        void queryClient.invalidateQueries({
          queryKey: ["customers", customer.id, "contacts"],
        });
      }
    },
  });
};

// Delete contact
export const useDeleteContact = () => {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`${API_URL}/api/v2/customers/me/address-book/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      if (customer?.id) {
        void queryClient.invalidateQueries({
          queryKey: ["customers", customer.id, "contacts"],
        });
      }
    },
  });
};

// Set default contact
export const useSetDefaultContact = () => {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/me/address-book/${id}/set-default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      if (customer?.id) {
        void queryClient.invalidateQueries({
          queryKey: ["customers", customer.id, "contacts"],
        });
      }
    },
  });
};

// Toggle favorite
export const useToggleFavoriteContact = () => {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/me/address-book/${id}/toggle-favorite`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: contactsKeys.detail(data.id),
      });
      if (customer?.id) {
        void queryClient.invalidateQueries({
          queryKey: ["customers", customer.id, "contacts"],
        });
      }
    },
  });
};

// List contacts for the logged-in customer
export const useCustomerContacts = (query?: ListContactsQuery) => {
  const { token, customer } = useSession();

  return useQuery({
    queryKey: ["customers", customer?.id, "contacts", query],
    queryFn: async (): Promise<ListContactsResponse> => {
      if (!customer?.id) {
        throw new Error("Customer ID is required");
      }

      const params = new URLSearchParams();

      if (query?.search) params.append("search", query.search);
      if (query?.isFavorite !== undefined)
        params.append("isFavorite", String(query.isFavorite));
      if (query?.label) params.append("label", query.label);
      if (query?.limit) params.append("limit", String(query.limit));
      if (query?.offset) params.append("offset", String(query.offset));

      const { data } = await axios.get<ListContactsResponse>(
        `${API_URL}/api/v2/customers/me/address-book?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!customer?.id,
  });
};

// Parse contact input using AI
export const useParseContact = () => {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (input: string): Promise<ParsedContactData> => {
      const { data } = await axios.post<ParsedContactData>(
        `${API_URL}/api/v2/address-book/parse`,
        { input },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
  });
};
